import React from "react";
import { createMuiTheme, ThemeProvider } from "@material-ui/core/styles";
import { isAdminUiSkinEnabled } from "adminUiSkin";

// ─────────────────────────────────────────────────────────────────────────────
// Design tokens
// ─────────────────────────────────────────────────────────────────────────────
const COLOR = {
    ink900:   "#111827",
    ink700:   "#374151",
    ink500:   "#6B7280",
    ink300:   "#D1D5DB",
    ink100:   "#F5F6F8",
    ink50:    "#F9FAFB",
    white:    "#ffffff",
    border:   "#E6E6EC",
    borderHover: "#D1D5DB",
    red:      "#EF4444",
    activeBar: "#111827",
};

const FONT = '"Inter", "Roboto", "Helvetica Neue", Arial, sans-serif';

// ─────────────────────────────────────────────────────────────────────────────
// Theme definition — SINGLE source of truth for all Shadcn-style overrides
// ─────────────────────────────────────────────────────────────────────────────
export const theme = createMuiTheme({
    // ── Shape ────────────────────────────────────────────────────────────────
    shape: { borderRadius: 8 },

    // ── Spacing (8px base) ───────────────────────────────────────────────────
    spacing: 8,

    // ── Palette ──────────────────────────────────────────────────────────────
    palette: {
        primary:    { main: COLOR.ink900, contrastText: COLOR.white },
        secondary:  { main: COLOR.ink100,  contrastText: COLOR.ink900 },
        background: { default: COLOR.ink50, paper: COLOR.white },
        text:       { primary: COLOR.ink900, secondary: COLOR.ink500 },
        divider:    COLOR.border,
        error:      { main: COLOR.red },
    },

    // ── Typography (Inter, 8px-grid scale) ───────────────────────────────────
    typography: {
        fontFamily: FONT,
        // "antialiased" via CSS in admin-shadcn.css body rule
        h1: { fontFamily: FONT, fontWeight: 700, fontSize: "32px", letterSpacing: "0", color: COLOR.ink900 },
        h2: { fontFamily: FONT, fontWeight: 700, fontSize: "28px", letterSpacing: "0", color: COLOR.ink900 },
        h3: { fontFamily: FONT, fontWeight: 700, fontSize: "24px", letterSpacing: "0", color: COLOR.ink900 },
        h4: { fontFamily: FONT, fontWeight: 600, fontSize: "20px", letterSpacing: "0", color: COLOR.ink900 },
        h5: { fontFamily: FONT, fontWeight: 600, fontSize: "16px", letterSpacing: "0", color: COLOR.ink900 },
        h6: { fontFamily: FONT, fontWeight: 600, fontSize: "14px", letterSpacing: "0", color: COLOR.ink900 },
        subtitle1: { fontFamily: FONT, fontWeight: 500, fontSize: "14px", color: COLOR.ink900 },
        subtitle2: { fontFamily: FONT, fontWeight: 500, fontSize: "12px", color: COLOR.ink500 },
        body1:     { fontFamily: FONT, fontWeight: 400, fontSize: "14px", color: COLOR.ink900 },
        body2:     { fontFamily: FONT, fontWeight: 400, fontSize: "13px", color: COLOR.ink500 },
        caption:   { fontFamily: FONT, fontWeight: 400, fontSize: "12px", color: COLOR.ink500 },
        overline:  { fontFamily: FONT, fontWeight: 600, fontSize: "11px", letterSpacing: "0.06em", textTransform: "uppercase" },
        button:    { fontFamily: FONT, fontWeight: 600, fontSize: "13px", textTransform: "none", letterSpacing: "0" },
    },

    // ── Component overrides ───────────────────────────────────────────────────
    overrides: {

        // ── Paper ─────────────────────────────────────────────────────────────
        MuiPaper: {
            root: {
                borderRadius: 12,
                boxShadow: "0 12px 30px rgba(17,24,39,0.05)",
                border: `1px solid ${COLOR.border}`,
            },
            elevation1: {
                boxShadow: "0 12px 30px rgba(17,24,39,0.05)",
            },
            elevation2: {
                boxShadow: "0 18px 42px rgba(17,24,39,0.07)",
            },
        },

        // ── Card ──────────────────────────────────────────────────────────────
        MuiCard: {
            root: {
                borderRadius: 16,
                boxShadow: "0 12px 30px rgba(17,24,39,0.05)",
                border: `1px solid ${COLOR.border}`,
            },
        },

        // ── Form layout ───────────────────────────────────────────────────────
        MuiFormControl: {
            root: {
                // Give every FormControl room for the floating label above
                marginTop: "24px !important",
                marginBottom: "8px !important",
            },
            marginNormal: {
                marginTop: "24px !important",
                marginBottom: "8px !important",
            },
            marginDense: {
                marginTop: "16px !important",
                marginBottom: "4px !important",
            },
        },

        // ── Input Label (the floating label) ─────────────────────────────────
        MuiInputLabel: {
            root: {
                fontFamily: FONT,
                fontSize: "12px",
                fontWeight: 500,
                color: COLOR.ink500,
                // Default (unfocused, unshrunk) sits above input by 16px
                transform: "translate(0, -24px) scale(1)",
                transformOrigin: "top left",
            },
            formControl: {
                top: 0,
                left: 0,
                position: "absolute",
                transform: "translate(0, -24px) scale(1)",
            },
            shrink: {
                transform: "translate(0, -24px) scale(0.85) !important",
                color: `${COLOR.ink500} !important`,
            },
            outlined: {
                transform: "translate(14px, 14px) scale(1)",
                "&$shrink": {
                    transform: "translate(14px, -6px) scale(0.75)",
                },
            },
        },

        // ── FormLabel (used by FormGroup labels, RadioGroup, etc.) ────────────
        MuiFormLabel: {
            root: {
                fontFamily: FONT,
                fontSize: "12px",
                fontWeight: 500,
                color: COLOR.ink500,
                "&$focused": { color: COLOR.ink900 },
            },
        },

        // ── FormHelperText ────────────────────────────────────────────────────
        MuiFormHelperText: {
            root: {
                fontFamily: FONT,
                fontSize: "11px",
                marginTop: 4,
                color: COLOR.ink500,
            },
        },

        // ── Input (underline / standard variant) ─────────────────────────────
        MuiInput: {
            root: {
                fontFamily: FONT,
                fontSize: "14px",
                height: 48,
                border: `1px solid ${COLOR.border}`,
                borderRadius: 10,
                backgroundColor: COLOR.white,
                padding: "0 12px",
                marginTop: "0 !important",
                boxSizing: "border-box",
                boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
                transition: "border-color 150ms ease, box-shadow 150ms ease",
                "&:hover": { borderColor: COLOR.borderHover },
                "&$focused": {
                    borderColor: COLOR.ink900,
                    boxShadow: "0 0 0 3px rgba(17,24,39,0.06)",
                },
            },
            // Remove the classic underline completely
            underline: {
                "&:before": { display: "none" },
                "&:after":  { display: "none" },
                "&:hover:not($disabled):before": { display: "none" },
            },
        },

        // ── InputBase ─────────────────────────────────────────────────────────
        MuiInputBase: {
            root: {
                fontFamily: FONT,
                fontSize: "14px",
                color: COLOR.ink900,
            },
            input: {
                padding: "10px 0",
                height: "auto",
                boxSizing: "border-box",
                "&::placeholder": { color: COLOR.ink500, opacity: 1 },
            },
        },

        // ── OutlinedInput ─────────────────────────────────────────────────────
        MuiOutlinedInput: {
            root: {
                fontFamily: FONT,
                height: 48,
                borderRadius: 10,
                backgroundColor: "#F7F7F9",
                "&:hover $notchedOutline": { borderColor: COLOR.borderHover },
                "&$focused $notchedOutline": { borderColor: COLOR.ink900, borderWidth: 1 },
            },
            notchedOutline: { borderColor: COLOR.border },
            input: { padding: "12px 14px" },
        },

        // ── Select ────────────────────────────────────────────────────────────
        MuiSelect: {
            root: { fontFamily: FONT, fontSize: "14px" },
            select: { "&:focus": { backgroundColor: "transparent" } },
            icon: { color: COLOR.ink500, top: "calc(50% - 10px)" },
        },

        // ── Autocomplete select paper ─────────────────────────────────────────
        MuiAutocomplete: {
            paper: { borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.08)" },
            option: {
                fontFamily: FONT,
                fontSize: "14px",
                padding: "8px 16px",
                "&[data-focus='true']": { backgroundColor: COLOR.ink100 },
                "&[aria-selected='true']": { backgroundColor: COLOR.ink100, fontWeight: 600 },
            },
        },

        // ── Menu / Popover ─────────────────────────────────────────────────────
        MuiMenu: {
            paper: { borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.08)", border: `1px solid ${COLOR.border}` },
        },
        MuiMenuItem: {
            root: {
                fontFamily: FONT,
                fontSize: "14px",
                padding: "8px 16px",
                color: COLOR.ink900,
                "&:hover": { backgroundColor: COLOR.ink100 },
                "&$selected": { backgroundColor: COLOR.ink100, fontWeight: 600 },
            },
        },

        // ── Button ────────────────────────────────────────────────────────────
        MuiButton: {
            root: {
                fontFamily: FONT,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                borderRadius: 8,
                minHeight: 40,
                padding: "0 16px",
                boxShadow: "none",
                transition: "background-color 150ms ease, color 150ms ease, border-color 150ms ease",
                "&:active": { transform: "scale(0.98)" },
            },
            contained: {
                backgroundColor: COLOR.ink900,
                color: COLOR.white,
                boxShadow: "none",
                "&:hover": { backgroundColor: COLOR.ink700, boxShadow: "none" },
                "&:active": { boxShadow: "none" },
            },
            outlined: {
                borderColor: COLOR.border,
                backgroundColor: COLOR.white,
                color: COLOR.ink900,
                "&:hover": { backgroundColor: COLOR.ink50, borderColor: COLOR.borderHover },
            },
            text: {
                color: COLOR.ink900,
                "&:hover": { backgroundColor: COLOR.ink50 },
            },
        },

        // ── IconButton ─────────────────────────────────────────────────────────
        MuiIconButton: {
            root: {
                borderRadius: 8,
                padding: 8,
                color: COLOR.ink900,
                transition: "background-color 150ms ease",
                "&:hover": { backgroundColor: COLOR.ink100 },
            },
        },

        // ── AppBar ────────────────────────────────────────────────────────────
        MuiAppBar: {
            root: {
                boxShadow: "none",
                borderBottom: `1px solid ${COLOR.border}`,
                backgroundColor: "rgba(255,255,255,0.85)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                color: COLOR.ink900,
            },
        },
        MuiToolbar: {
            root: { minHeight: "64px !important" },
            regular: { minHeight: "64px !important" },
        },

        // ── Badge ─────────────────────────────────────────────────────────────
        MuiBadge: {
            badge: {
                fontFamily: FONT,
                fontWeight: 600,
                backgroundColor: COLOR.red,
                color: COLOR.white,
                minWidth: 18,
                height: 18,
                borderRadius: 9,
                fontSize: "10px",
                border: `2px solid ${COLOR.white}`,
            },
            dot: { width: 8, height: 8, minWidth: 8, borderRadius: "50%", border: "none" },
        },

        // ── List / Sidebar items ───────────────────────────────────────────────
        MuiList: {
            root: { padding: 0 },
            padding: { paddingTop: 0, paddingBottom: 0 },
        },
        MuiListItem: {
            root: {
                fontFamily: FONT,
                borderRadius: 8,
                margin: "2px 0",
                padding: "0 12px",
                height: 40,
                minHeight: 40,
                color: COLOR.ink500,
                transition: "background-color 150ms ease, color 150ms ease",
                "&:hover": {
                    backgroundColor: COLOR.ink50,
                    color: COLOR.ink900,
                },
                "&$selected": {
                    backgroundColor: "#EEF0F3",
                    color: COLOR.ink900,
                    fontWeight: 600,
                    borderLeft: `3px solid ${COLOR.activeBar}`,
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    paddingLeft: "9px", // 12 - 3px border
                    "&:hover": { backgroundColor: "#EEF0F3" },
                },
            },
            gutters: { paddingLeft: 12, paddingRight: 12 },
        },
        MuiListItemText: {
            root: { margin: 0 },
            primary: {
                fontFamily: FONT,
                fontSize: "13px",
                fontWeight: 500,
                lineHeight: "40px",
                color: "inherit",
            },
            secondary: {
                fontFamily: FONT,
                fontSize: "12px",
                color: COLOR.ink500,
            },
        },
        MuiListItemIcon: {
            root: {
                minWidth: 32,
                color: "inherit",
            },
        },
        MuiCollapse: {
            container: { paddingLeft: 16 },
        },

        // ── Divider ───────────────────────────────────────────────────────────
        MuiDivider: {
            root: { backgroundColor: COLOR.border },
        },

        // ── Chip ──────────────────────────────────────────────────────────────
        MuiChip: {
            root: {
                fontFamily: FONT,
                fontSize: "12px",
                fontWeight: 500,
                height: 28,
                borderRadius: 6,
            },
        },

        // ── Tabs ──────────────────────────────────────────────────────────────
        MuiTabs: {
            root: { minHeight: 40 },
            indicator: { backgroundColor: COLOR.ink900, height: 2, borderRadius: 2 },
        },
        MuiTab: {
            root: {
                fontFamily: FONT,
                textTransform: "none",
                fontWeight: 500,
                fontSize: "13px",
                minHeight: 40,
                padding: "6px 16px",
                color: COLOR.ink500,
                "&$selected": { color: COLOR.ink900, fontWeight: 600 },
            },
        },

        // ── Table ─────────────────────────────────────────────────────────────
        MuiTableHead: {
            root: {},
        },
        MuiTableCell: {
            root: {
                fontFamily: FONT,
                fontSize: "13px",
                borderBottom: `1px solid ${COLOR.border}`,
                padding: "12px 16px",
            },
            head: {
                fontWeight: 600,
                fontSize: "12px",
                color: COLOR.ink500,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                backgroundColor: COLOR.ink50,
            },
            body: {
                color: COLOR.ink900,
                fontSize: "14px",
            },
        },
        MuiTableRow: {
            root: {
                "&:last-child td": { borderBottom: 0 },
                "&:hover": { backgroundColor: COLOR.ink50 },
            },
        },

        // ── Dialog ────────────────────────────────────────────────────────────
        MuiDialog: {
            paper: { borderRadius: 16 },
        },
        MuiDialogTitle: {
            root: {
                fontFamily: FONT,
                fontWeight: 600,
                fontSize: "16px",
                padding: "24px 24px 8px",
            },
        },
        MuiDialogContent: {
            root: { padding: "8px 24px 16px" },
        },
        MuiDialogActions: {
            root: { padding: "8px 24px 24px", gap: 8 },
        },

        // ── Checkbox / Radio / Switch ─────────────────────────────────────────
        MuiCheckbox: {
            root: {
                color: COLOR.border,
                "&:hover": { backgroundColor: "rgba(17,24,39,0.04)" },
            },
            colorPrimary: { "&$checked": { color: COLOR.ink900 } },
        },
        MuiRadio: {
            root: {
                color: COLOR.border,
                "&:hover": { backgroundColor: "rgba(17,24,39,0.04)" },
            },
            colorPrimary: { "&$checked": { color: COLOR.ink900 } },
        },
        MuiSwitch: {
            colorPrimary: {
                "&$checked": { color: COLOR.ink900 },
                "&$checked + $track": { backgroundColor: COLOR.ink900, opacity: 1 },
            },
        },

        // ── Tooltip ───────────────────────────────────────────────────────────
        MuiTooltip: {
            tooltip: {
                fontFamily: FONT,
                fontSize: "12px",
                backgroundColor: COLOR.ink900,
                color: COLOR.white,
                borderRadius: 6,
                padding: "4px 10px",
            },
        },

        // ── Snackbar ──────────────────────────────────────────────────────────
        MuiSnackbarContent: {
            root: {
                fontFamily: FONT,
                borderRadius: 10,
                backgroundColor: COLOR.ink900,
                color: COLOR.white,
            },
        },

        // ── CssBaseline ───────────────────────────────────────────────────────
        MuiCssBaseline: {
            "@global": {
                html: { WebkitFontSmoothing: "antialiased", MozOsxFontSmoothing: "grayscale" },
                body: {
                    fontFamily: FONT,
                    fontSize: "14px",
                    lineHeight: 1.5,
                    color: COLOR.ink900,
                    backgroundColor: COLOR.ink50,
                },
                "*, *::before, *::after": { boxSizing: "border-box" },
                a: { color: COLOR.ink900, textDecoration: "none" },
            },
        },
    },
});

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────
export default function AdminThemeProvider({ children }) {
    const isShadcn = isAdminUiSkinEnabled();

    if (!isShadcn) {
        return <>{children}</>;
    }

    return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}
